import {get} from "lodash";
import {getBoolean, getInteger, getObject, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated with this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'customerGroups';

// Define reducer types handled by this reducer
export const REDUCER_TYPES = {
	RESET: '@customerGroups/reset',
	SET_LIST_DATA: '@customerGroups/set_list_data',
	SET_LIST_FILTER: '@customerGroups/set_list_filter',
	CLEAR_LIST_DATA: '@customerGroups/clear_list_data',
	SET_ITEM: '@customerGroups/set_item',
	CLEAR_ITEM: '@customerGroups/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} customerGroupsListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setCustomerGroupsListData: customerGroupsListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: customerGroupsListData
	}),
	setCustomerGroupsListFilter: customerGroupsListFilter => ({
		type: REDUCER_TYPES.SET_LIST_FILTER, 
		filter: customerGroupsListFilter
	}),
	clearCustomerGroupsListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {CustomerGroupsItemDataObject} customerGroupsItem
	 * @return {{type: string, item: CustomerGroupsItemDataObject}}
	 */
	setCustomerGroupsItem: customerGroupsItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: customerGroupsItem
	}),
	clearCustomerGroupsItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export an initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {CustomerGroupsItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.SET_LIST_FILTER: return {
			...state, 
			listData: {...getObject(state.listData), filter: action.filter}
		};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getCustomerGroupsList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getCustomerGroupsListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getCustomerGroupsListSort: state => ({
		sortBy: get(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getCustomerGroupsListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getCustomerGroupsItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;